import Graph from 'graphology';
import { bfsFromNode } from 'graphology-traversal';
import {
  NetworkTopologyElementsEnum,
  NetworkTopologyLocation,
  NetworkTopologyLocationCity,
  NetworkTopologyMapConfig,
  NetworkTopologyElement,
  NetworkTopologyStateEnum,
  NetworkTopologyFilter,
  NetworkTopologyFilterServerPayload,
  NetworkTopologyMapData,
  Connection,
  PoiXcipioNodeMap,
  NetworkElementProps
} from './network-topology.types';

export default class NeGraph {
  graph = new Graph({ multi: true, allowSelfLoops: false, type: 'directed' });
  afIdtoDataMap: { [id: string]: NetworkTopologyElement } = {};
  cfIdtoDataMap: { [id: string]: NetworkTopologyElement } = {};
  xcipioIdtoDataMap: { [id: string]: NetworkTopologyElement } = {};
  ccpagIdtoDataMap: { [id: string]: NetworkTopologyElement } = {};
  mdf2IdtoDataMap: { [id: string]: NetworkTopologyElement } = {};
  mdf3IdtoDataMap: { [id: string]: NetworkTopologyElement } = {};
  admfIdtoDataMap: { [id: string]: NetworkTopologyElement } = {};

  constructor(private mapData: NetworkTopologyMapData) {
    this.buildLookups();
    this.buildGraph();
  }

  buildLookups() {
    this.buildIdToDataMap(this.mapData.accessFunctions, this.afIdtoDataMap);
    this.buildIdToDataMap(this.mapData.collectionFunctions, this.cfIdtoDataMap);
    this.buildIdToDataMap(this.mapData.xcipioNodes, this.xcipioIdtoDataMap);
    this.buildIdToDataMap(this.mapData.ccpags, this.ccpagIdtoDataMap);
    this.buildIdToDataMap(this.mapData.mdf2s, this.mdf2IdtoDataMap);
    this.buildIdToDataMap(this.mapData.mdf3s, this.mdf3IdtoDataMap);
    this.buildIdToDataMap(this.mapData.admfs, this.admfIdtoDataMap);
  }

  buildIdToDataMap(
    data: any[],
    idToDataMap: { [id: string]: NetworkElementProps }
  ) {
    data.forEach((ne) => {
      idToDataMap[ne.data.id] = ne;
    });
  }

  buildGraph() {
    this.addNodes();
    this.addEdges();
  }

  addNodes() {
    [
      ...this.mapData.accessFunctions,
      ...this.mapData.collectionFunctions,
      ...this.mapData.xcipioNodes,
      ...this.mapData.ccpags,
      ...this.mapData.mdf2s,
      ...this.mapData.mdf3s,
      ...this.mapData.admfs
    ].forEach((ne) => {
      this.graph.addNode(ne.data.id, ne);
    });
  }

  addEdges() {
    [
      ...this.mapData.accessFunctions,
      ...this.mapData.xcipioNodes,
      ...this.mapData.ccpags,
      ...this.mapData.mdf2s,
      ...this.mapData.mdf3s,
      ...this.mapData.admfs
    ].forEach((ne) => {
      const xcipioConnections =
        ne?.connections[NetworkTopologyElementsEnum.Xcipio] || [];
      const afConnections =
        ne?.connections[NetworkTopologyElementsEnum.AccessFunction] || [];
      const cfConnections =
        ne?.connections[NetworkTopologyElementsEnum.CollectionFunction] || [];
      [...xcipioConnections, ...afConnections, ...cfConnections]
        .flatMap((x) => x)
        .forEach((con: Connection) => {
          if (
            this.graph.hasNode(ne.data.id) &&
            this.graph.hasNode(con.destinationId)
          ) {
            this.graph.addDirectedEdge(ne.data.id, con.destinationId, con);
          }
        });
    });
  }

  getDataById(nodeId: string): NetworkTopologyElement {
    return (
      this.afIdtoDataMap[nodeId] ||
      this.cfIdtoDataMap[nodeId] ||
      this.xcipioIdtoDataMap[nodeId] ||
      this.ccpagIdtoDataMap[nodeId] ||
      this.mdf2IdtoDataMap[nodeId] ||
      this.mdf3IdtoDataMap[nodeId] ||
      this.admfIdtoDataMap[nodeId]
    );
  }

  getAllConnections() {
    const ret = [];
    const itr = this.graph.edgeEntries();
    let result = itr.next();
    while (!result.done) {
      ret.push(result.value);
      result = itr.next();
    }
    return ret;
  }

  getAllNodesInPathFromNodeToLeaf(
    nodeId: string,
    mode: 'in' | 'out' = 'out'
  ): NetworkTopologyMapData {
    const nodeData = this.getDataById(nodeId);
    if (nodeData.type === NetworkTopologyElementsEnum.AccessFunction) {
      return this.mapToFilterResult(this.getNodesInPathForAf(nodeId));
    }
    if (nodeData.type === NetworkTopologyElementsEnum.Xcipio) {
      if (nodeData.data.nodeType === 'CCPAG') {
        return this.mapToFilterResult(this.getNodesInPathForCCPAG(nodeId));
      }
      if (nodeData.data.nodeType === 'MDF3') {
        return this.mapToFilterResult(this.getNodesInPathForMDF3(nodeId));
      }
      if (nodeData.data.nodeType === 'MDF2') {
        return this.mapToFilterResult(this.getNodesInPathForMDF2(nodeId));
      }
      return this.mapToFilterResult(this.getNodesInPathForXcipio(nodeId));
    }
    if (nodeData.type === NetworkTopologyElementsEnum.CollectionFunction) {
      return this.mapToFilterResult(this.getNodesInPathForCf(nodeId));
    }
    return {
      accessFunctions: [],
      collectionFunctions: [],
      xcipioNodes: [],
      ccpags: [],
      mdf2s: [],
      mdf3s: [],
      admfs: []
    };
  }

  mapToFilterResult(nodes: any[]): NetworkTopologyMapData {
    const ret: NetworkTopologyMapData = {
      accessFunctions: [],
      collectionFunctions: [],
      xcipioNodes: [],
      ccpags: [],
      mdf2s: [],
      mdf3s: [],
      admfs: []
    };
    nodes.forEach((n) => {
      if (n.type === NetworkTopologyElementsEnum.AccessFunction) {
        ret.accessFunctions.push(n);
      }
      if (n.type === NetworkTopologyElementsEnum.CollectionFunction) {
        ret.collectionFunctions.push(n);
      }
      if (n.type === NetworkTopologyElementsEnum.Xcipio) {
        if (n.data.nodeType === 'CCPAG') {
          ret.ccpags.push(n);
        } else if (n.data.nodeType === 'MDF2') {
          ret.mdf2s.push(n);
        } else if (n.data.nodeType === 'MDF3') {
          ret.mdf3s.push(n);
        } else if (n.data.nodeType === 'ADMF') {
          ret.admfs.push(n);
        } else {
          ret.xcipioNodes.push(n);
        }
      }
    });
    return ret;
  }

  getNodesInPathForCCPAG(nodeId: string) {
    const nodeIds = [];
    nodeIds.push(...this.graph.inNeighbors(nodeId));
    bfsFromNode(this.graph, nodeId, (node, attr, depth) => {
      nodeIds.push(node);
    });
    // remove any duplicates
    const unique = new Set(nodeIds);
    const ret: any[] = [];
    unique.forEach((id) => {
      ret.push(this.getDataById(id));
    });
    return ret;
  }

  getNodesInPathForMDF3(nodeId: string) {
    const nodeIds = [];
    nodeIds.push(...this.graph.outNeighbors(nodeId));
    bfsFromNode(
      this.graph,
      nodeId,
      (node, attr, depth) => {
        nodeIds.push(node);
      },
      { mode: 'in' }
    );
    // remove any duplicates
    const unique = new Set(nodeIds);
    const ret: any[] = [];
    unique.forEach((id) => {
      ret.push(this.getDataById(id));
    });
    return ret;
  }

  getNodesInPathForMDF2(nodeId: string) {
    const nodeIds = [];
    nodeIds.push(...this.graph.outNeighbors(nodeId));
    bfsFromNode(
      this.graph,
      nodeId,
      (node, attr, depth) => {
        if ((attr.data.nodeType !== 'XCP' && attr.data.nodeType !== 'MDF3' && attr.data.nodeType !== 'MDF2') || nodeId === node) {
          nodeIds.push(node);
        }
      },
      { mode: 'in' }
    );
    // remove any duplicates
    const unique = new Set(nodeIds);
    const ret: any[] = [];
    unique.forEach((id) => {
      ret.push(this.getDataById(id));
    });
    return ret;
  }

  getNodesInPathForXcipio(nodeId: string) {
    const nodeIds = [];
    nodeIds.push(...this.graph.outNeighbors(nodeId));
    bfsFromNode(
      this.graph,
      nodeId,
      (node, attr, depth) => {
      if (
        attr.type !== NetworkTopologyElementsEnum.AccessFunction ||
        nodeId === node
      ) {
        nodeIds.push(node);
      }
    });
    // remove any duplicates
    const unique = new Set(nodeIds);
    const ret: any[] = [];
    unique.forEach((id) => {
      ret.push(this.getDataById(id));
    });
    return ret;
  }

  getNodesInPathForAf(nodeId: string) {
    const nodeIds = [];
    nodeIds.push(...this.graph.inNeighbors(nodeId));
    bfsFromNode(this.graph, nodeId, (node, attr, depth) => {
      if (
        attr.type !== NetworkTopologyElementsEnum.AccessFunction ||
        nodeId === node
      ) {
        nodeIds.push(node);
      }
    });
    // remove any duplicates
    const unique = new Set(nodeIds);
    const ret: any[] = [];
    unique.forEach((id) => {
      ret.push(this.getDataById(id));
    });
    return ret;
  }

  getNodesInPathForCf(nodeId: string) {
    const nodeIds: string[] = [];
    bfsFromNode(
      this.graph,
      nodeId,
      (node, attr, depth) => {
        nodeIds.push(node);
      },
      {
        mode: 'in'
      }
    );
    // remove any duplicates
    const unique = new Set(nodeIds);
    const ret: any[] = [];
    unique.forEach((id) => {
      ret.push(this.getDataById(id));
    });
    return ret;
  }
}
